/*
 * Based on https://github.com/UstymUkhman/color-grading/blob/master/src/glsl/grading.frag
 * MIT License
 * @author Ustym Ukhman / https://github.com/UstymUkhman
 *
 * Modifications copyright Higharc Inc. 2019
 */
import { DataTexture, LinearFilter, RGBAFormat, ShaderChunk } from "three";

ShaderChunk["color_grading_lut_params"] = `
uniform sampler2D colorGradingLUT;

#ifndef COLOR_GRADING_LUT_RESOLUTION
#define COLOR_GRADING_LUT_RESOLUTION 16.0
#endif

// Get a graded color value from a LUT.
// The LUT that's essentially a 3D texture is stored as a series of slices in a 2D texture.
// The function assumes that the resolutions of the 3 dimensions of the LUT are equal.
// Wrapping is handled by clamping.
// The sample value is interpolated across all dimensions.
vec4 gradeColor(vec4 color) {
	vec3 uvw = clamp(color.rgb, 0.0, 1.0);

	float sliceWidthInTexCoords = 1.0 / COLOR_GRADING_LUT_RESOLUTION;
	float innerWidth = COLOR_GRADING_LUT_RESOLUTION - 1.0;

	vec2 pixelSizeInTexCoords = vec2(sliceWidthInTexCoords / COLOR_GRADING_LUT_RESOLUTION, 1.0 / COLOR_GRADING_LUT_RESOLUTION);
	vec2 sliceInnerSize = pixelSizeInTexCoords * innerWidth;

	float zSlice0 = min(floor(uvw.z * innerWidth), innerWidth);
	float zSlice1 = min(zSlice0 + 1.0, innerWidth);

	vec2 slice0SampleCoords = vec2(zSlice0 * sliceWidthInTexCoords, 0.0);
	vec2 slice1SampleCoords = vec2(zSlice1 * sliceWidthInTexCoords, 0.0);

	// We offset the x/y coordinates by half a pixel so that interpolation only happens only inside the slice
	// and not between neighboring slices. This also ensures that we start interpolating as soon as the uvw value is
	// not 0.0 or 1.0.
	vec2 xyInsideSlice = uvw.xy * sliceInnerSize + pixelSizeInTexCoords * 0.5;
	slice0SampleCoords += xyInsideSlice;
	slice1SampleCoords += xyInsideSlice;

	vec4 slice0Color = texture2D(colorGradingLUT, slice0SampleCoords);
	vec4 slice1Color = texture2D(colorGradingLUT, slice1SampleCoords);

	float zMixWeight = mod(uvw.z * innerWidth, 1.0);
	vec3 interpolatedRGB = mix(slice0Color, slice1Color, zMixWeight).rgb;
	return vec4(interpolatedRGB, color.a);
}`;

export const generateIdentityColorLUT = function (resolution = 16) {
  const width = resolution * resolution;
  const height = resolution;
  const pixelCount = width * height;
  const data = new Uint8Array(4 * pixelCount);
  for (let y = 0; y < resolution; ++y) {
    const emerald = y / (resolution - 1);
    for (let z = 0; z < resolution; ++z) {
      const blue = z / (resolution - 1);
      for (let x = 0; x < resolution; ++x) {
        const pixelIndex = y * width + z * resolution + x;
        const dataIndex = pixelIndex * 4;
        const red = x / (resolution - 1);
        data[dataIndex] = Math.round(red * 255);
        data[dataIndex + 1] = Math.round(emerald * 255);
        data[dataIndex + 2] = Math.round(blue * 255);
        data[dataIndex + 3] = 255;
      }
    }
  }
  const texture = new DataTexture(data, width, height, RGBAFormat);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
};
